"use client";

import { useLingui } from "@lingui/react/macro";
import { Skeleton } from "@offstreetparking/design-system/feedback";
import { OFFSTREET_COMPANY_LOGO, ThemeProvider } from "@offstreetparking/design-system/providers";
import { Text } from "@offstreetparking/design-system/typography";
import { ContentCard } from "@offstreetparking/portal-flow/components/layouts/ContentCard";
import { useSearchParams } from "next/navigation";
import { useMemo } from "react";
import * as z from "zod/v4";
import { CommonLayout } from "~/components/layouts/CommonLayout";
import { useCompany } from "~/components/providers/CompanyProvider";
import { offstreetClient } from "~/services/offstreetApi";
type SupportConfirmationProps = {
	children: React.ReactNode;
};
const queryParamsSchema = z.object({
	eventId: z.string().min(1).optional().catch(undefined),
	identifier: z.string().min(1).optional().catch(undefined),
});
export const SupportConfirmation = ({ children }: SupportConfirmationProps) => {
	const { t } = useLingui();
	const searchParams = useSearchParams();
	const eventId = queryParamsSchema.parse({
		eventId: searchParams.get("eventId"),
	}).eventId;
	const identifier = queryParamsSchema.parse({
		identifier: searchParams.get("identifier"),
	}).identifier;
	const { company } = useCompany();
	const { data, isLoading } = offstreetClient.permitType.getBrandingForPermitTypes.useQuery(
		{
			eventId,
			identifier,
		},
		{
			enabled: !!eventId || !!identifier,
			retry: false,
		},
	);
	const companyLogo = useMemo(() => {
		if (company?.name && company?.branding?.companyLogoFileUrl) {
			return {
				url: company.branding.companyLogoFileUrl,
				altText: company.name,
			};
		}
		if (isLoading) {
			return undefined;
		}
		if (data?.branding?.companyLogoFile?.publicUrl) {
			return {
				url: data.branding.companyLogoFile.publicUrl,
				altText: data.whiteLabel?.portalName ?? "Offstreet",
			};
		}
		if (data?.whiteLabel?.portalLogo) {
			return {
				url: data.whiteLabel.portalLogo,
				altText: data.whiteLabel?.portalName ?? "Offstreet",
			};
		}
		return {
			url: OFFSTREET_COMPANY_LOGO,
			altText: "Offstreet",
		};
	}, [company, data, isLoading]);
	const branding = useMemo(() => {
		if (company?.branding) {
			return company.branding;
		}
		if (isLoading || !data?.branding) {
			return undefined;
		}
		return {
			id: 0,
			primaryColorRamp: data.branding.primaryColorRamp,
			accentColorRamp: data.branding.accentColorRamp,
		};
	}, [company, data, isLoading]);
	return (
		<CommonLayout
			srOnlyTitle={`${t`Thanks for`} ${t`parking with us!`}`}
			infoSlot={
				isLoading ? (
					<div className="flex flex-col gap-2">
						<Skeleton height={48} width={300} />
						<Skeleton height={48} width={500} />
						<Skeleton height={24} width={520} />
					</div>
				) : (
					<div className="space-y-8 pt-8">
						<div className="break-words font-extrabold text-5xl">
							<Text weight="bolder" size="5xl">
								{t`Thanks for`}
							</Text>
							<Text weight="bolder" size="5xl" intent="primary">
								{t`parking with us!`}
							</Text>
						</div>

						<Text size="sm">
							{t({
								message: "Search for your session by entering your confirmation number",
								comment:
									"Parking session — one vehicle's stay, looked up by confirmation number. Not a login session.",
							})}
						</Text>
					</div>
				)
			}
			companyLogo={companyLogo}
		>
			<ThemeProvider branding={branding} />
			<ContentCard>
				{isLoading && (
					<div className="flex flex-col gap-2 p-4">
						<Skeleton height={48} width={300} />
						<Skeleton height={48} width={500} />
						<Skeleton height={24} width={520} />
					</div>
				)}
				{!isLoading && children}
			</ContentCard>
		</CommonLayout>
	);
};
